import { useCallback, useEffect, useRef, useState } from 'react';
import './interviewerAvatar.css';

/**
 * The Napster talking avatar that reads each question aloud (puppeteer mode: it says only what we
 * send with `talk`). The SDK is the vendored standalone build in public/vendor, loaded on demand;
 * the npm package needs Redux Toolkit 2 and we are on 1.x.
 *
 * Optional by design: if the backend says it's off, or anything fails, `status` ends up 'off' and
 * the page works exactly as it did without an avatar.
 */
const SDK_DIR = `${process.env.PUBLIC_URL}/vendor/napster-companion-api-2.0.0`;
// An idle session closes after 3 minutes; the candidate can be recording for that long.
const KEEPALIVE_MS = 45 * 1000;
// Never leave the candidate waiting on a lost `ended` event.
const talkTimeoutMs = (text: string) => 15000 + text.length * 120;

export type AvatarStatus = 'off' | 'connecting' | 'ready';

interface NapsterEvent {
  event: string;
  data?: { state?: string; talk_id?: string; [key: string]: unknown };
}

interface NapsterInstance {
  destroy: () => void;
  muteMic: () => void;
  sendCommand: (command: { type: string; data: unknown }) => void;
}

interface NapsterSdk {
  init: (getToken: () => Promise<string>, config: Record<string, unknown>) => Promise<NapsterInstance>;
}

let sdkLoading: Promise<NapsterSdk> | null = null;

const loadSdk = (): Promise<NapsterSdk> => {
  if (!sdkLoading) {
    sdkLoading = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = `${SDK_DIR}/index.css`;
      document.head.appendChild(css);
      const script = document.createElement('script');
      script.src = `${SDK_DIR}/index.standalone.js`;
      script.async = true;
      script.onload = () => resolve((window as unknown as { napsterCompanionApiSDK: NapsterSdk }).napsterCompanionApiSDK);
      script.onerror = () => { sdkLoading = null; reject(new Error('avatar SDK failed to load')); };
      document.body.appendChild(script);
    });
  }
  return sdkLoading;
};

// The tenant's brand colour (app.css sets the default), for the SDK's loader and buttons.
const brandColor = () =>
  getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim() || '#038912';

const SESSION_ENDING = new Set(['session_expired', 'no_credits_left', 'provider_connection_aborted']);

/**
 * `getToken` asks our backend for a connection token, or null when the avatar is off.
 * Mount the avatar by passing `containerRef` to an element; it connects while `active`.
 */
export const useInterviewerAvatar = (active: boolean, getToken: () => Promise<string | null>) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<AvatarStatus>(active ? 'connecting' : 'off');
  const [speaking, setSpeaking] = useState(false);
  const instance = useRef<NapsterInstance | null>(null);
  const pendingTalk = useRef<{ id: string; done: () => void } | null>(null);
  const tokenFn = useRef(getToken);
  tokenFn.current = getToken;

  const finishTalk = useCallback((id?: string) => {
    const talk = pendingTalk.current;
    if (talk && (!id || talk.id === id)) {
      pendingTalk.current = null;
      talk.done();
    }
  }, []);

  useEffect(() => {
    if (!active || !container) return undefined;
    let cancelled = false;
    let keepalive: ReturnType<typeof setInterval> | undefined;
    const goOff = () => {
      if (keepalive) clearInterval(keepalive);
      finishTalk();
      instance.current?.destroy();
      instance.current = null;
      if (!cancelled) setStatus('off');
    };
    setStatus('connecting');

    (async () => {
      try {
        const sdk = await loadSdk();
        if (cancelled) return;
        const created = await sdk.init(async () => {
          const token = await tokenFn.current();
          if (!token) throw new Error('avatar is off');
          return token;
        }, {
          layout: { type: 'inline', mountContainer: container },
          style: { view: 'rectangle', accentColor: brandColor() },
          features: {
            controls: { enabled: false },
            inactiveTimeout: { enabled: false },
            disclaimer: { enabled: true, text: 'AI interviewer' },
          },
          onAvatarReady: () => {
            // The candidate answers through our own recorder; a live mic here would cut Holly off.
            instance.current?.muteMic();
            if (!cancelled) setStatus('ready');
          },
          onData: (message: NapsterEvent) => {
            if (message.event === 'talk_state_changed') {
              const { state, talk_id: talkId } = message.data ?? {};
              if (state === 'ended' || state === 'canceled') finishTalk(talkId);
            } else if (SESSION_ENDING.has(message.event)) {
              goOff();
            }
          },
          onError: goOff,
        });
        if (cancelled) { created.destroy(); return; }
        instance.current = created;
        created.muteMic();
        keepalive = setInterval(() => {
          instance.current?.sendCommand({
            type: 'send_message',
            data: { role: 'system', text: 'The candidate is still answering.', trigger_response: false },
          });
        }, KEEPALIVE_MS);
      } catch {
        goOff();
      }
    })();

    return () => {
      cancelled = true;
      goOff();
    };
  }, [active, container, finishTalk]);

  /** Speak one line. Resolves when it has been said (or could not be), never rejects. */
  const speak = useCallback((text: string): Promise<void> => {
    const avatar = instance.current;
    if (!avatar || status !== 'ready') return Promise.resolve();
    finishTalk();
    const id = `q-${Date.now()}`;
    setSpeaking(true);
    return new Promise<void>((resolve) => {
      const timer = setTimeout(() => finishTalk(id), talkTimeoutMs(text));
      pendingTalk.current = {
        id,
        done: () => { clearTimeout(timer); setSpeaking(false); resolve(); },
      };
      avatar.sendCommand({ type: 'talk', data: { talkId: id, text } });
    });
  }, [status, finishTalk]);

  return { containerRef: setContainer, status, speaking, speak };
};
